/**
 * Store de defaults custom para eventos ANMAT por tupla.
 *
 * El catálogo SNT define cientos de eventos. Para cada operación nueva, el
 * sistema infiere el evento default por la tupla `(informador, origen, destino)`
 * usando texto hardcoded (`DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR` o
 * `RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR`).
 *
 * Si una organización quiere usar OTRO evento default para alguna tupla
 * específica (ej. usar "DISTRIBUCION DEL PRODUCTO PARA OBRA SOCIAL" en lugar
 * del genérico), un administrador puede setearlo como custom acá. Persiste a
 * localStorage para sobrevivir a reloads.
 *
 * Las views (PickeoView, TrazadoDirectoView, ConfirmarRecepcionView) leen este
 * mapa y se lo pasan a `inferirEventoAnmat` — la función ya estaba preparada
 * desde Fase 1.
 *
 * Regulatorio: cada cambio queda en audit con `evento_anmat.set_default` o
 * `evento_anmat.quitar_default` y razón opcional.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { EventosDefaultCustom } from '@/domain/anmat';
import { eventoAnmatDefaultKey } from '@/domain/anmat';
import type { TipoActor } from '@/domain/types';

interface EventosDefaultState {
  /** Map de tupla → id de evento custom. Tuplas no presentes usan el hardcoded. */
  defaults: EventosDefaultCustom;

  setDefault: (
    informador: TipoActor | null | undefined,
    origen: TipoActor | null | undefined,
    destino: TipoActor | null | undefined,
    eventoId: number,
  ) => void;
  removeDefault: (
    informador: TipoActor | null | undefined,
    origen: TipoActor | null | undefined,
    destino: TipoActor | null | undefined,
  ) => void;
  clearAll: () => void;
  /** Devuelve el id custom seteado para una tupla, o undefined si no hay. */
  getDefault: (
    informador: TipoActor | null | undefined,
    origen: TipoActor | null | undefined,
    destino: TipoActor | null | undefined,
  ) => number | undefined;
}

export const useEventosDefaultStore = create<EventosDefaultState>()(
  persist(
    (set, get) => ({
      defaults: {},

      setDefault: (informador, origen, destino, eventoId) => {
        const clave = eventoAnmatDefaultKey(informador, origen, destino);
        set((s) => ({ defaults: { ...s.defaults, [clave]: eventoId } }));
      },

      removeDefault: (informador, origen, destino) => {
        const clave = eventoAnmatDefaultKey(informador, origen, destino);
        set((s) => {
          const next = { ...s.defaults };
          delete next[clave];
          return { defaults: next };
        });
      },

      clearAll: () => set({ defaults: {} }),

      getDefault: (informador, origen, destino) => {
        const clave = eventoAnmatDefaultKey(informador, origen, destino);
        return get().defaults[clave];
      },
    }),
    {
      name: 'riga-traza-eventos-default',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
